import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { EVAL_AGENTS } from "../../agents";

function IconChevronLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function IconCaret() {  return (    <svg width="10" height="8" viewBox="0 0 10 8" fill="#0f141a">      <path d="M5 8L0 0h10L5 8z" />    </svg>  );}

interface AgentOption {
  id: string;
  label: string;
}

interface NavItemDef {
  to: string;
  labelKey: `sidebar.${"homepage" | "reports" | "usage" | "insights" | "tasks" | "datasets" | "evaluators"}`;
  indent?: boolean;
}

const MAIN_NAV: NavItemDef[] = [
  { to: "/eval", labelKey: "sidebar.homepage" },
];

const DETAILS_NAV: NavItemDef[] = [
  { to: "/eval/reports", labelKey: "sidebar.reports", indent: true },
  { to: "/eval/usage", labelKey: "sidebar.usage", indent: true },
  { to: "/eval/insights", labelKey: "sidebar.insights", indent: true },
  { to: "/eval/tasks", labelKey: "sidebar.tasks", indent: true },
  { to: "/eval/datasets", labelKey: "sidebar.datasets", indent: true },
];

const BOTTOM_NAV: NavItemDef[] = [
  { to: "/eval/evaluators", labelKey: "sidebar.evaluators", indent: true },
];

interface EvalSidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  agent: string;
  onAgentChange: (agent: string) => void;
}

function AgentDropdown({ agent, onAgentChange, agents }: { agent: string; onAgentChange: (a: string) => void; agents: AgentOption[] }) {  const { t } = useTranslation("eval");  const [open, setOpen] = useState(false);  const ref = useRef<HTMLDivElement>(null);  const label = agents.find((a) => a.id === agent)?.label || "Rocky";  useEffect(() => {    if (!open) return;    const handler = (e: MouseEvent) => {      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);    };    document.addEventListener("mousedown", handler);    return () => document.removeEventListener("mousedown", handler);  }, [open]);  return (    <div className="eval-sidebar__agent-dropdown" ref={ref}>      <button className="eval-sidebar__agent-trigger" onClick={() => setOpen(!open)}>        <span className="eval-sidebar__agent-label">{t("agentTitle", { agent: label })}</span>        <IconCaret />      </button>      {open && (        <ul className="eval-sidebar__agent-menu">          {agents.map((a) => (            <li              key={a.id}              className={"eval-sidebar__agent-option" + (a.id === agent ? " eval-sidebar__agent-option--active" : "")}              onClick={() => { onAgentChange(a.id); setOpen(false); }}            >              {a.label}            </li>          ))}        </ul>      )}    </div>  );}
export default function EvalSidebar({ collapsed = true, onToggle, agent, onAgentChange }: EvalSidebarProps) {
  const { t } = useTranslation("eval");

  if (collapsed) {
    return (
      <aside className="eval-sidebar eval-sidebar--collapsed">
        <div className="eval-sidebar__collapsed-toggle">
          <button
            className="eval-sidebar__toggle-btn"
            onClick={onToggle}
            title={t("sidebar.expand")}
          >
            <IconMenu />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="eval-sidebar eval-sidebar--expanded">
      {/* Top: Agent selector + collapse button */}
      <div className="eval-sidebar__header">
        <AgentDropdown agent={agent} onAgentChange={onAgentChange} agents={EVAL_AGENTS} />
        <button
          className="eval-sidebar__collapse-btn"
          onClick={onToggle}
          title={t("sidebar.collapse")}
        >
          <IconChevronLeft />
        </button>
      </div>

      {/* Main navigation */}
      <nav className="eval-sidebar__nav">
        {MAIN_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            className={({ isActive }) =>
              "eval-sidebar__link" + (isActive ? " eval-sidebar__link--active" : "")
            }
          >
            {t(item.labelKey)}
          </NavLink>
        ))}

        <p className="eval-sidebar__group-label">{t("sidebar.details")}</p>
        {DETAILS_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              "eval-sidebar__link eval-sidebar__link--indent" + (isActive ? " eval-sidebar__link--active" : "")
            }
          >
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="eval-sidebar__bottom">
        <p className="eval-sidebar__group-label">{t("sidebar.platform")}</p>
        {BOTTOM_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              "eval-sidebar__link eval-sidebar__link--indent" + (isActive ? " eval-sidebar__link--active" : "")
            }
          >
            {t(item.labelKey)}
          </NavLink>
        ))}
      </div>
    </aside>
  );
}
